import React, { useMemo } from "react";
import { Button } from "components/common/Button";
import { resolveDatasetColor } from "@/lib/datasetColor";
import {
  spaceColor,
  spaceTrail,
  useOrgContextStore,
  visibleSpaces,
} from "@/components/main/stores/orgContextStore";

/// Heads the Overview with the space it is narrowed to: the path back out on
/// top ("All spaces" clears the filter, an ancestor crumb narrows to it), the
/// space itself as the page's title. Deliberately NOT a `SectionRule` — it
/// heads the page rather than a section, so no hairline and nothing trails it.
/// A narrowed overview never looks like an org that lost its models: the stat
/// strip below stays org-wide.
export const SpaceFilterRule: React.FC<{
  spaceId: string;
  onSelect: (spaceId: string | null) => void;
}> = ({ spaceId, onSelect }) => {
  const spaces = useOrgContextStore((s) => s.spaces);
  const trail = useMemo(
    () => spaceTrail(visibleSpaces(spaces), spaceId),
    [spaces, spaceId],
  );
  if (trail.length === 0) return null;
  const active = trail[trail.length - 1];
  const ancestors = trail.slice(0, -1);

  return (
    // Two rows over one 20px icon column: the swatch is placed in the TITLE
    // row, so it centres on the space's name instead of drifting with the
    // path above it. The column geometry is the section rules' (4px pad, 20px
    // icon, 8px gap), which is what puts the name, the path and every label
    // below on the same x.
    <div
      className="grid items-center px-1"
      style={{
        gridTemplateColumns: "20px minmax(0, 1fr)",
        columnGap: "var(--spacing-sm)",
      }}
    >
      <span
        className="inline-flex items-center gap-1.5"
        style={{ gridColumn: 2 }}
      >
        <Button
          variant="quiet-link"
          size="xs"
          onClick={() => onSelect(null)}
          data-testid="clear-space-filter"
        >
          All spaces
        </Button>
        {ancestors.map((space) => (
          <React.Fragment key={space.space_id}>
            <span aria-hidden className="text-fg-muted opacity-50">
              /
            </span>
            <Button
              variant="quiet-link"
              size="xs"
              onClick={() => onSelect(space.space_id)}
            >
              {space.name}
            </Button>
          </React.Fragment>
        ))}
      </span>
      <span
        className="flex items-center justify-center"
        style={{ gridColumn: 1, gridRow: 2 }}
      >
        <span
          aria-hidden
          className="h-2.5 w-2.5 shrink-0 rounded-app"
          style={{ backgroundColor: resolveDatasetColor(spaceColor(active)) }}
        />
      </span>
      <h1
        className="text-fg-title leading-tight truncate"
        style={{ gridColumn: 2, gridRow: 2 }}
      >
        {active.name}
      </h1>
    </div>
  );
};
